/**
 * `EmptyState` (W1-12 ui-kit-i18n; contract EmptyStateProps): what a panel shows when it has nothing yet (no
 * problems, no lines, empty library): an icon, a title, an optional sentence and command buttons. Actions run
 * through the UiBridge with their args; the first action is the primary one, and each shows its shortcut and
 * disabled reason like any command button.
 */

import { useTr, useUiBridge } from './bridge';
import { Button } from './Button';
import s from './EmptyState.module.css';
import { Icon } from './Icon';
import { cx } from './internal/cx';
import type { EmptyStateProps } from './types';

export function EmptyState(props: EmptyStateProps & { readonly className?: string | undefined }) {
  const tr = useTr();
  const bridge = useUiBridge();
  const actions = props.actions ?? [];
  return (
    <div className={cx(s.empty, props.className)} role="status">
      <span className={s.icon}>
        <Icon name={props.icon} size={32} />
      </span>
      <p className={s.title}>{tr(props.title)}</p>
      {props.body !== undefined && <p className={s.body}>{tr(props.body)}</p>}
      {actions.length > 0 && (
        <div className={s.actions}>
          {actions.map((a, i) => (
            <Button
              key={`${a.command}:${String(i)}`}
              variant={i === 0 ? 'primary' : 'secondary'}
              command={a.command}
              onClick={() => {
                bridge.run(a.command, a.args);
              }}
            >
              {tr(a.label)}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
